import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTrackOrigin } from './hooks';
import { api, type Me } from './api';
import { Badge as UiBadge, Button, MazuMark } from '@ledgerloom/ui';
import { Icon, Loading } from './components/ui';
import { clearParties } from './components/PartyName';
import { hasSurface, SessionCtx, type Stats } from './session';
import { HelpButton } from './components/HelpButton';
import { Folded, groupFirst, isCurrent, SideNav, visibleDests, type Badge, type Dest } from './nav';
import { Login } from './pages/Login';
import { SetPassword } from './pages/SetPassword';
import { MfaSetup } from './pages/MfaSetup';
import { Welcome } from './pages/Welcome';
import { SetupPanel, SetupStart } from './pages/SetupPanel';
import { NoticesHere } from './components/Notices';
import { Home } from './pages/Home';
import { Contacts } from './pages/Contacts';
import { ContactDetail } from './pages/ContactDetail';
import { ContactNew } from './pages/ContactNew';
import { Approvals } from './pages/Approvals';
import { Activity, ProposalDetail } from './pages/Activity';
import { Import } from './pages/Import';
import { Agents } from './pages/Agents';
import { AgentHire } from './pages/AgentHire';
import { AgentDetail } from './pages/AgentDetail';
import { RunTrace } from './pages/RunTrace';
import { AgentBuild } from './pages/AgentBuild';
import { Leads } from './pages/Leads';
import { Pipeline } from './pages/Pipeline';
import { DealDetail, Deals } from './pages/Deals';
import { TicketDetail, Tickets } from './pages/Tickets';
import { TicketSettings } from './pages/TicketSettings';
import { Products, QuoteDetail, QuoteNew, Quotes } from './pages/Quotes';
import { Marketing } from './pages/Marketing';
import { AssetView, Library } from './pages/Library';
import { DashboardNew, Dashboards, DashboardView } from './pages/Dashboards';
import { Admin, ConnectionsSettings, DataSettings, PeopleSettings, SecuritySettings, SendingSettings, WorkspaceSettings } from './pages/Admin';
import { ContactSettings } from './pages/ContactSettings';
import { RolesPage } from './pages/Roles';
import { BusinessModel } from './pages/BusinessModel';
import { More } from './pages/More';
import { Help } from './pages/Help';
import { InMotion } from './pages/Team';
import { Desk, DeskChat } from './pages/Desk';
import { PersonDetail } from './pages/PersonDetail';
import { ConsultDetail, Consults, GroupDetailPage, Groups, Signals, TraceView, Traces, WorkflowEdit, Workflows } from './pages/AgentWork';
import { Ledger } from './pages/Ledger';
import { ChartOfAccounts, Journal, Statements, TrialBalance } from './pages/Books';
import { ParallelRun } from './pages/ParallelRun';
import { Calendar } from './pages/Calendar';
import { Mail } from './pages/Mail';

// Phone width shows four tabs; More reaches the rest.
const tab = (to: string, label: string, icon: string, badge?: Badge): Dest => ({ to, label, icon, match: [to], exact: to === '/', badge });
const TABS: Dest[] = [tab('/', 'Home', 'home'), tab('/desk', 'Agent Desk', 'team', 'approvals')];
TABS[1]!.match.push('/agents', '/team');
const BADGE_SR: Record<string, string> = { approvals: 'waiting for you' };

// The sidebar folds to icons; the choice is this browser's, so storage that
// throws (a private window) just means it starts open.
const NAV_KEY = 'll.nav.collapsed';
// A contact is edited in place on its own page; the old edit link lands there.
function EditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/contacts/${id}?tab=about`} replace />;
}

// The Inbox and Team pages became the Agent Desk (ADR 0039): old links land there, an open item or tab kept.
function DeskRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (params.get('tab') === 'motion') { params.delete('tab'); return <Navigate to={`/desk/in-motion${params.size ? `?${params}` : ''}`} replace />; }
  return <Navigate to={`${to}${search}`} replace />;
}

function useCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(NAV_KEY) === '1'; } catch { return false; } });
  const toggle = useCallback(() => setCollapsed((c) => {
    try { localStorage.setItem(NAV_KEY, c ? '0' : '1'); } catch { /* not remembered */ }
    return !c;
  }), []);
  return [collapsed, toggle];
}

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [stats, setStats] = useState<Stats | null>(null);
  const [collapsed, toggleNav] = useCollapsed();
  const location = useLocation();
  useTrackOrigin();
  const setupStep = new URLSearchParams(location.search).get('setup');

  useEffect(() => {
    api<Me>('/v1/me').then(setMe).catch(() => setMe(null));
    const out = () => setMe(null);
    window.addEventListener('ll:signed-out', out);
    return () => window.removeEventListener('ll:signed-out', out);
  }, []);

  // Another person (or none) is signed in: drop the contact names the last one could see.
  const who = me ? `${me.tenant_id}:${me.user_id}` : null;
  useEffect(() => { clearParties(); }, [who]);

  const refreshStats = useCallback(() => { api<Stats>('/v1/stats').then(setStats).catch(() => undefined); }, []);
  useEffect(() => {
    if (!me) return;
    refreshStats();
    const t = setInterval(refreshStats, 15_000);
    return () => clearInterval(t);
  }, [me, refreshStats, location.pathname]);

  const refreshMe = useCallback(() => { api<Me>('/v1/me').then(setMe).catch(() => undefined); }, []);
  // A hire or retirement, here or signed by someone else, adds or takes away the pieces its agent brings.
  useEffect(() => { refreshMe(); }, [location.pathname, refreshMe]);
  // The welcome interview opens once per sign-in while it is offered or under way; Later or No thanks lets the owner go.
  const welcomed = useRef(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!me) { welcomed.current = false; return; }
    if (welcomed.current || me.password_change_required || !hasSurface(me, 'setup') || !(me.onboarding === 'offered' || me.onboarding === 'started')) return;
    welcomed.current = true;
    if (location.pathname !== '/welcome') navigate('/welcome', { replace: true });
  }, [me, location.pathname, navigate]);

  const signOut = useCallback(async () => {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    setMe(null);
  }, []);

  if (me === undefined) return <Loading label="Starting Mazu" />;
  // The login answer is the session's person only; /v1/me adds what the app gates on (mfa_required, features, surfaces).
  if (me === null) return <Login onSignedIn={refreshMe} />;
  if (me.password_change_required) return <SetPassword me={me} onDone={setMe} onSignOut={signOut} />;
  if (me.mfa_required && !me.mfa_verified) return <MfaSetup me={me} onSignOut={signOut} />;

  // More lists every destination without a tab, and is current on any of them.
  const more = visibleDests(me).filter((d) => !TABS.some((t) => t.to === d.to));
  const moreTab: Dest = { to: '/more', label: 'More', icon: 'more', match: ['/more', ...more.flatMap((d) => d.match)] };
  const here = visibleDests(me).find((d) => isCurrent(location.pathname, d))?.label;
  const badge = (b?: Badge) => (b === 'approvals' ? (stats?.inbox ?? stats?.my_approvals) : undefined);
  const count = (b?: Badge) => {
    const n = badge(b);
    return n ? <UiBadge className="count min-w-5 justify-center bg-[var(--attention)] font-bold text-[var(--attention-foreground)]">{n}<span className="sr-only"> {BADGE_SR[b!]}</span></UiBadge> : null;
  };

  return (
    <SessionCtx.Provider value={{ me, stats, refreshStats, refreshMe, signOut }}>
      <a className="skip-link" href="#main">Skip to content</a>
      <div className={`shell${collapsed ? ' nav-collapsed' : ''}`}>
        <aside className="sidebar">
          <div className="brand"><span aria-hidden={!collapsed || undefined}><MazuMark className="size-[26px]" /></span><span className="nav-label" aria-hidden="true">Mazu</span></div>
          <SideNav me={me} collapsed={collapsed} count={count} />
          <div className="sidebar-foot">
            <Folded on={collapsed} label="Expand the menu">
              <Button type="button" variant="ghost" size="sm" className="nav-toggle" aria-expanded={!collapsed} aria-controls="main-nav" onClick={toggleNav}>
                <Icon name="panel" /><span className="nav-label">{collapsed ? 'Expand the menu' : 'Collapse the menu'}</span>
              </Button>
            </Folded>
            {!collapsed && <>
              <div className="who">{me.display_name}</div>
              <div className="faint">{me.email} · {me.role}</div>
              <div className="faint">{me.tenant_name}</div>
            </>}
            <Folded on={collapsed} label="Sign out">
              <Button type="button" variant="ghost" size="sm" className="sign-out" style={collapsed ? undefined : { marginTop: 8, paddingLeft: 0 }} onClick={signOut}>
                {collapsed && <Icon name="logout" />}<span className="nav-label">Sign out</span>
              </Button>
            </Folded>
          </div>
        </aside>
        <div className="shell-main">
        {/* The top bar: where you are, and the Mazu head that opens help (brand standard, 2026-11-02). */}
        <header className="topbar">
          <span className="topbar-here">{here}</span>
          <HelpButton />
        </header>
        <main id="main" tabIndex={-1}>
          {/* The setup walkthrough opens each screen it changes with its step beside it (onboarding v2, phase 6). */}
          <div className={setupStep ? 'with-setup' : 'contents'}>
          {setupStep && <SetupPanel areaKey={setupStep} />}
          <div className={setupStep ? 'setup-screen' : 'contents'}>
          <NoticesHere />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={hasSurface(me, 'setup') ? <Welcome /> : <Navigate to="/" replace />} />
            <Route path="/welcome/setup" element={hasSurface(me, 'setup') ? <SetupStart /> : <Navigate to="/" replace />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/new" element={<ContactNew />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/contacts/:id/edit" element={<EditRedirect />} />
            <Route path="/desk" element={<Desk />} />
            <Route path="/desk/needs-you" element={<Approvals />} />
            <Route path="/desk/in-motion" element={<InMotion />} />
            <Route path="/desk/chat" element={<DeskChat />} />
            <Route path="/desk/chat/:id" element={<DeskChat />} />
            <Route path="/approvals" element={<DeskRedirect to="/desk/needs-you" />} />
            <Route path="/inbox" element={<DeskRedirect to="/desk/needs-you" />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/mail" element={<Mail />} />
            <Route path="/activity" element={<Activity />} />
            <Route path="/activity/:id" element={<ProposalDetail />} />
            <Route path="/import" element={<Import />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/hire" element={<AgentHire />} />
            <Route path="/agents/build" element={hasSurface(me, 'agent_builder') ? <AgentBuild /> : <Navigate to="/agents" replace />} />
            <Route path="/agents/groups" element={<Groups />} />
            <Route path="/agents/groups/:id" element={<GroupDetailPage />} />
            <Route path="/agents/workflows" element={<Workflows />} />
            <Route path="/agents/workflows/edit" element={<WorkflowEdit />} />
            <Route path="/agents/signals" element={<Signals />} />
            <Route path="/agents/consults" element={<Consults />} />
            <Route path="/agents/consults/:id" element={<ConsultDetail />} />
            <Route path="/agents/traces" element={<Traces />} />
            <Route path="/agents/traces/:id" element={<TraceView />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
            <Route path="/agents/:id/runs/:runId" element={<RunTrace />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/:id" element={<TicketDetail />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/deals/:id" element={<DealDetail />} />
            <Route path="/quotes" element={<Quotes />} />
            <Route path="/quotes/new" element={<QuoteNew />} />
            <Route path="/quotes/:id" element={<QuoteDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/library" element={<Library />} />
            <Route path="/library/:id" element={<AssetView />} />
            <Route path="/dashboards" element={<Dashboards />} />
            <Route path="/dashboards/new" element={<DashboardNew />} />
            <Route path="/dashboards/:id" element={<DashboardView />} />
            <Route path="/ledger" element={<Ledger />} />
            <Route path="/books/accounts" element={<ChartOfAccounts />} />
            <Route path="/books/journal" element={<Journal />} />
            <Route path="/books/trial-balance" element={<TrialBalance />} />
            <Route path="/books/statements" element={<Statements />} />
            <Route path="/books/parallel-run" element={<ParallelRun />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/security" element={<SecuritySettings />} />
            <Route path="/admin/workspace" element={<WorkspaceSettings />} />
            <Route path="/admin/people" element={<PeopleSettings />} />
            <Route path="/admin/sending" element={<SendingSettings />} />
            <Route path="/admin/connections" element={<ConnectionsSettings />} />
            <Route path="/admin/data" element={<DataSettings />} />
            <Route path="/admin/roles" element={<RolesPage />} />
            <Route path="/admin/contacts" element={<ContactSettings />} />
            <Route path="/admin/tickets" element={<TicketSettings />} />
            <Route path="/admin/business-model" element={<BusinessModel />} />
            <Route path="/team" element={<DeskRedirect to="/desk" />} />
            <Route path="/team/people/:id" element={<PersonDetail />} />
            <Route path="/work" element={<Navigate to={groupFirst(me, 'work')} replace />} />
            <Route path="/records" element={<Navigate to={groupFirst(me, 'records')} replace />} />
            <Route path="/books" element={<Navigate to={groupFirst(me, 'books')} replace />} />
            <Route path="/more" element={<More nav={more} />} />
            <Route path="/help" element={hasSurface(me, 'help') ? <Help /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
          </div>
        </main>
        </div>
        <nav className="tabbar" aria-label="Main">
          {[...TABS, moreTab].map((n) => {
            const on = isCurrent(location.pathname, n);
            return (
              <Link key={n.to} to={n.to} className={on ? 'active' : undefined} aria-current={on ? 'page' : undefined}>
                <Icon name={n.icon} />{n.label}
                {count(n.badge)}
              </Link>
            );
          })}
        </nav>
      </div>
    </SessionCtx.Provider>
  );
}
